import clsx from 'clsx';
import dot from 'dot-object';
import type { SelectButtonChangeEvent } from 'primereact/selectbutton';
import { SelectButton as PrimeReactSelectButton } from 'primereact/selectbutton';
import { useEffect, useRef, useState } from 'react';
import useOdinMemo from '../../../common/hooks/use-odin-memo';
import useTestTag from '../../../common/hooks/use-test-tag';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import { forceAssert, getContrastTextColor, isCheckboxGroupResult, isValidHexColor } from '../../common-functions';
import type { DynamicFieldComponentProps, DynamicFieldDataProps } from '../../dynamic-form/dynamic-form.interfaces';
import type { JsonData, JsonDataItem } from '../../generic-types';
import ErrorLabel from '../../labels/error-label';
import type { CheckboxGroupResult } from '../checkbox-group/checkbox-group.component';

export interface SelectButtonProps
  extends DynamicFieldComponentProps<Array<CheckboxGroupResult>>,
    DynamicFieldDataProps {
  showError?: boolean;
}

const SelectButton = ({
  label,
  value = [],
  data,
  textField,
  valueField,
  inputRef,
  onChange,
  readOnly,
  required,
  error,
  disabled,
  showError = true,
}: SelectButtonProps) => {
  const [selectedValues, setSelectedValues] = useState(value?.map((item) => item.value));
  const contrastColorCache = useRef<Map<string, string>>(new Map());
  const tagCreator = useTestTag(label || 'select-button');

  const optionData = useOdinMemo(
    () =>
      forceAssert<JsonData>(data).map<CheckboxGroupResult>((item) => {
        return {
          value: dot.pick(valueField, item),
          text: dot.pick(textField, item),
          color: dot.pick('color', item),
        };
      }),
    [data]
  );

  useEffect(() => {
    setSelectedValues(value?.map((item) => item.value));
  }, [value]);

  const handleChange = (event: SelectButtonChangeEvent) => {
    const values = event.value as Array<unknown>;
    const selectedObjects = values
      .map<CheckboxGroupResult>((item: CheckboxGroupResult | unknown) => {
        if (isCheckboxGroupResult(item)) {
          return item;
        } else {
          const foundOption = forceAssert<JsonData>(data).find((option) => dot.pick(valueField, option) === item);
          if (foundOption) {
            return {
              value: item,
              text: dot.pick(textField, foundOption),
            };
          }

          return {
            value: null,
            text: null,
          };
        }
      })
      .filter((option) => option.value !== null && option.text !== null);

    onChange?.(selectedObjects);
    setSelectedValues(selectedObjects.map((item) => item.value));
  };

  const commonButtonStyles = clsx(
    'sm:w-auto text-md leading-5 rounded-sm focus:outline-hidden justify-center transition-colors duration-200 min-w-[3rem] min-h-[2.25rem] py-2 px-5 mr-4'
  );

  const customOptionTemplate = (option: JsonDataItem) => {
    const optionValue = dot.pick('value', option);
    const optionText = dot.pick('text', option) as string;
    const optionColor = dot.pick('color', option) as string;
    const isValidColor = optionColor && isValidHexColor(optionColor);
    const isSelected = selectedValues?.indexOf(optionValue) > -1;
    const hasCustomSelectedBg = isSelected && isValidColor;

    //different styles depending on selected or not
    const buttonStyles = clsx(
      commonButtonStyles,
      {
        'min-w-5 h-9 rounded-sm bg-gray-4 px-2.5 text-sm': !isSelected,
      },
      {
        'min-w-5 h-9 rounded-sm text-mono-1 px-2.5 text-sm': isSelected,
        'bg-primary-2': isSelected && !hasCustomSelectedBg,
      },
      { 'opacity-70': readOnly || disabled }
    );

    let contrastColor: string | undefined;
    if (hasCustomSelectedBg) {
      const cached = contrastColorCache.current.get(optionColor);
      if (cached) {
        contrastColor = cached;
      } else {
        contrastColor = getContrastTextColor(optionColor);
        contrastColorCache.current.set(optionColor, contrastColor);
      }
    }

    const style = hasCustomSelectedBg ? { backgroundColor: optionColor, color: contrastColor } : undefined;

    return (
      <button
        type="button"
        value={optionValue as string}
        className={buttonStyles}
        style={style}
        ref={tagCreator(optionText?.replace(/\s+/g, '-').toLowerCase())}
      >
        {optionText}
      </button>
    );
  };

  return (
    <>
      <div className="flex items-center gap-2 p-1">
        <RequiredIndicator readOnly={readOnly} required={required} />
        <PrimeReactSelectButton
          onChange={handleChange}
          value={selectedValues}
          itemTemplate={customOptionTemplate}
          options={optionData}
          optionLabel={textField}
          optionValue="value"
          disabled={readOnly || disabled}
          ref={inputRef}
          multiple
          className="flex flex-col space-y-2"
        />
      </div>
      {error && showError && <ErrorLabel>{error}</ErrorLabel>}
    </>
  );
};

export default SelectButton;
